import React, { useEffect, useMemo, useState } from "react";

import { SorterResult, PaginationConfig } from "antd/lib/table";
import flatten from "lodash/flatten";

import {
  DEAL_CATEGORY_TYPES,
  DEAL_CATEGORIES,
  DEAL_CATEGORY_FILTERS_MAPPING,
} from "manager/config/constants";
import {
  useGetPropertiesWithInvitation,
  useGetDeals,
  useGetDealsFast,
  useGetDealsCounts,
  useGetDistinctLumenResults,
  useCompanyAssignableEmployees,
} from "manager/hooks/api";
import { MenuItem } from "shared/components/TablePageCardHeader/interfaces";
import { DEFAULT_PAGINATION } from "shared/config/constants";
import { useActiveCompany, usePermissions } from "shared/hooks";
import { useDealStatuses, useDealCategories } from "shared/hooks/api";
import { PaginationProps } from "shared/interfaces";
import { getCachedActiveCompanyId } from "shared/utils/auth";
import { useVeroFlag } from "shared/utils/featureFlags";
import { useIsGBReady } from "shared/utils/growthbook";
import { VeroFlag } from "shared/utils/veroFlags";

import {
  LumenScore,
  LumenLabelByScore,
} from "../../components/app-summary/action-bar";

import {
  blankFilters,
  combineFilters,
  dealsFiltersStorage,
  getInitialOrderingColumn,
  dealsCountsStorage,
  KEYS,
  ORDER_BY,
} from "./config";
import Deals from "./Deals";
import { DealsCounts, Filters } from "./interfaces";
import { shouldUseDjangoDealsList } from "./blockedByIdFraud";
import { sanitizeSentilinkRiskStatusFilter } from "./sentilinkRiskStatus";
import {
  buildRecommendationStatusOptions,
  sanitizeRecommendationStatusFilter,
} from "./vero1ReviewStatus";

interface DealsProps {
  experianPrompt?: boolean;
}

const DealsContainer = ({ experianPrompt }: DealsProps) => {
  const enableSearchService = useVeroFlag(VeroFlag.enableSearchService);
  const useSearchService = useVeroFlag(VeroFlag.useSearchService);
  const isGrowthBookReady = useIsGBReady();
  const { isIdFraudRiskEnabled: sentilinkFiltersActive } = usePermissions();
  // TI-2464: gate the batched /deals-fast/ endpoint behind a company-scoped
  // GrowthBook flag. companyId + companySlug are already attached to every
  // flag evaluation via useVeroGrowthBook (see shared/utils/growthbook.tsx),
  // so targeting rules by company work out of the box. We only trust the
  // flag once GrowthBook has finished loading; until then, and for any
  // company where the flag is off, we stay on the legacy /deals/ endpoint.
  const dealListApiEnhancementEnabled = useVeroFlag(
    VeroFlag.useDealListApiEnhancementTI2464
  );
  const useFastDealListEndpoint =
    isGrowthBookReady && dealListApiEnhancementEnabled;
  const [showSearchServiceResults, setShowSearchServiceResults] =
    useState(useSearchService);
  const { activeCompany, isActiveCompanyLoading } = useActiveCompany();
  const vero1FiltersActive = !!activeCompany?.isVero1Client;

  const storedFilters =
    dealsFiltersStorage.getItem(KEYS.filters) || blankFilters;
  const initialFilters: Filters = {
    ...storedFilters,
    sentilinkRiskStatus: sanitizeSentilinkRiskStatusFilter(
      storedFilters.sentilinkRiskStatus
    ),
    recommendationStatus: sanitizeRecommendationStatusFilter(
      storedFilters.recommendationStatus
    ),
  };
  const initialCategory =
    dealsFiltersStorage.getItem(KEYS.category) || DEAL_CATEGORY_TYPES.PENDING;
  const initialPagination =
    dealsFiltersStorage.getItem(KEYS.pagination) || DEFAULT_PAGINATION;
  const initialOrdering = dealsFiltersStorage.getItem(KEYS.ordering) || "";
  const initialOrderingColumn = getInitialOrderingColumn(initialOrdering);
  const initialShowArchived =
    dealsFiltersStorage.getItem(KEYS.showArchived) || false;
  const initialShowOnlyPaidFees =
    typeof dealsFiltersStorage.getItem(KEYS.showOnlyPaidFees) === "boolean"
      ? dealsFiltersStorage.getItem(KEYS.showOnlyPaidFees)
      : false;
  const initialDealsCounts =
    dealsCountsStorage.getItem(KEYS.dealsCounts) || DEAL_CATEGORIES;

  const [category, setCategory] = useState<string>(initialCategory);
  const [showArchived, setShowArchived] =
    useState<boolean>(initialShowArchived);
  const [showOnlyPaidFees, setShowOnlyPaidFees] = useState<boolean>(
    initialShowOnlyPaidFees
  );
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [pagination, setPagination] =
    useState<PaginationProps>(initialPagination);
  const [ordering, setOrdering] = useState(initialOrdering);
  const [dealsFilters, setDealsFilters] = useState(
    combineFilters({
      category,
      filters,
      pagination,
      ordering,
      showArchived,
      showOnlyPaidFees,
      sentilinkFiltersActive: false,
      vero1FiltersActive: false,
    })
  );
  const [isPropertiesSettled, setPropertiesSettled] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(initialDealsCounts);

  const { data: dealCategories, isLoading: isDealCategoriesLoading } =
    useDealCategories();

  const { data: dealStatuses, isLoading: isDealStatusesLoading } =
    useDealStatuses();

  const { properties, isLoading: isPropertiesLoading } =
    useGetPropertiesWithInvitation();

  const { data: agents = [], isLoading: isAgentsLoading } =
    useCompanyAssignableEmployees(activeCompany?.id, {
      enabled: !isActiveCompanyLoading && !!activeCompany?.id,
    });

  const isFiltersLoading =
    isPropertiesLoading || isDealStatusesLoading || isAgentsLoading;

  const companyId = getCachedActiveCompanyId();

  const {
    data: distinctLumenResults,
    isLoading: isDistinctLumenResultsLoading,
  } = useGetDistinctLumenResults(companyId, {
    enabled: !isFiltersLoading && isPropertiesSettled && !!companyId,
  });

  // Both hooks are called unconditionally to comply with the rules of hooks;
  // `enabled` on each mirrors the flag decision so only the selected endpoint
  // actually fetches. We deliberately do NOT gate on `isGrowthBookReady` here:
  // in react-query v2 a disabled query with no cache is idle (`isLoading` and
  // `isFetching` both false), so gating both queries on GB readiness caused a
  // brief empty-applications placeholder to flash once filters/counts settled
  // before either endpoint ran. Instead we lean on `useVeroFlag`'s built-in
  // guard (it returns the default `false` until `gb.ready === true`), so the
  // legacy query fires immediately, and we only ever switch to the fast query
  // once GrowthBook has affirmatively evaluated the flag as true. In the flag-
  // on path this costs one extra legacy call during the GB init window, which
  // is a smaller price than either a permanent spinner (if GB never becomes
  // ready) or an empty-state flash.
  const blockedOverlayActive =
    category === DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD;
  const useDjangoDealsList = shouldUseDjangoDealsList({
    showSearchServiceResults,
    blockedByIdFraudFilterActive: blockedOverlayActive,
  });

  const dealsQueryEnabled =
    !isFiltersLoading && isPropertiesSettled && useDjangoDealsList;

  const legacyDealsQuery = useGetDeals(dealsFilters, {
    enabled: dealsQueryEnabled && !useFastDealListEndpoint,
  });
  const fastDealsQuery = useGetDealsFast(dealsFilters, {
    enabled: dealsQueryEnabled && useFastDealListEndpoint,
  });

  const {
    data: deals,
    isLoading: isDealsLoading,
    refetch: refetchDeals,
    isFetching: isDealsFetching,
  } = useFastDealListEndpoint ? fastDealsQuery : legacyDealsQuery;

  const { sentilinkBlocked: _sentilinkBlocked, ...dealsCountFilters } =
    dealsFilters;

  const {
    data: dealsCounts,
    isLoading: isDealsCountsLoading,
    isFetching: isDealsCountsFetching,
  } = useGetDealsCounts(dealsCountFilters, {
    enabled:
      !isFiltersLoading && isPropertiesSettled && useDjangoDealsList,
  });

  const updateMenuItemsCounts = (dealsCounts: DealsCounts, dealsFilters) => {
    const { dealStatus, dealCategory } = dealsFilters;

    setMenuItems((prevMenuItems: MenuItem[]): MenuItem[] => {
      let items: MenuItem[];

      if (dealStatus && dealCategory) {
        items = prevMenuItems
          .filter(
            (item) => item.key !== DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD
          )
          .map(
            (item: MenuItem): MenuItem =>
              item.key === dealCategory
                ? { ...item, count: dealsCounts[dealCategory] }
                : item
          );
      } else {
        items = DEAL_CATEGORIES.map(
          (category): MenuItem => ({
            key: category.key,
            name: category.name,
            count: dealsCounts[category.key],
          })
        );
      }

      if (sentilinkFiltersActive) {
        const blockedCard: MenuItem = {
          key: DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD,
          name: "Blocked by Identity Risk Check",
          count: dealsCounts.blockedByIdFraud ?? 0,
          accent: true,
        };
        const incompleteIndex = items.findIndex(
          (item) => item.key === DEAL_CATEGORY_TYPES.INCOMPLETE
        );
        const withoutBlocked = items.filter(
          (item) => item.key !== DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD
        );
        if (incompleteIndex >= 0) {
          withoutBlocked.splice(incompleteIndex + 1, 0, blockedCard);
          items = withoutBlocked;
        } else {
          items = [...withoutBlocked, blockedCard];
        }
      }

      dealsCountsStorage.addItem(KEYS.dealsCounts, items);
      return items;
    });
  };

  const updateFilters = (values?: Filters) => {
    if (!values) {
      setFilters({ ...blankFilters });
      return;
    }

    setFilters({
      ...filters,
      ...values,
    });
  };

  const updatedAllFilters = (filterValues) => {
    setFilters({ ...filters, ...filterValues });
    setPagination({ ...DEFAULT_PAGINATION });
  };

  const onTableChange = (
    _tablePagination: PaginationConfig,
    _tableFilters: Filters,
    sorter: SorterResult<Filters>
  ) => {
    const newOrdering = sorter?.order
      ? `${ORDER_BY[sorter.columnKey][sorter.order]}`
      : undefined;
    setOrdering(newOrdering);
  };

  useEffect(() => {
    if (properties) {
      if (
        Number(filters.propertyId) &&
        !properties.some(
          (property) => property.id === Number(filters.propertyId)
        )
      ) {
        const cleanFilters = {
          ...filters,
          propertyId: [],
        };

        setFilters(cleanFilters);
      }

      setPropertiesSettled(true);
    }
  }, [properties]);

  useEffect(() => {
    // Only clear persisted SentiLink filters once the company has loaded and is not entitled.
    if (!activeCompany || sentilinkFiltersActive) {
      return;
    }

    if ((filters.sentilinkRiskStatus || []).length) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        sentilinkRiskStatus: [],
      }));
    }

    if (category === DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD) {
      setCategory(DEAL_CATEGORY_TYPES.INCOMPLETE);
    }
  }, [
    activeCompany,
    sentilinkFiltersActive,
    filters.sentilinkRiskStatus,
    category,
  ]);

  useEffect(() => {
    if (dealsCounts) {
      updateMenuItemsCounts(dealsCounts, dealsFilters);
    }
  }, [dealsCounts, sentilinkFiltersActive]);

  /** NOTE: Whenever we update filters or category,
   * we should reset the pagination to avoid invalid page calls */
  useEffect(() => {
    setPagination({ ...DEFAULT_PAGINATION });
    dealsFiltersStorage.addItem(KEYS.filters, filters);
    dealsFiltersStorage.addItem(KEYS.category, category);
    dealsFiltersStorage.addItem(KEYS.showArchived, showArchived);
    dealsFiltersStorage.addItem(KEYS.showOnlyPaidFees, showOnlyPaidFees);
  }, [category, filters, showArchived, showOnlyPaidFees]);

  // Only pagination/ordering trigger refetch params. Filter/toggle changes reset page
  // in the effect above first, so the next render combines fresh filters with page 1.
  useEffect(() => {
    setDealsFilters(
      combineFilters({
        category,
        filters,
        pagination,
        ordering,
        showArchived,
        showOnlyPaidFees,
        sentilinkFiltersActive,
        vero1FiltersActive,
      })
    );

    dealsFiltersStorage.addItem(KEYS.pagination, pagination);
    dealsFiltersStorage.addItem(KEYS.ordering, ordering);
  }, [pagination, ordering, sentilinkFiltersActive, vero1FiltersActive]);

  const dealStatusSelection = useMemo(() => {
    const statusCategory =
      category === DEAL_CATEGORY_TYPES.BLOCKED_BY_ID_FRAUD
        ? DEAL_CATEGORY_TYPES.INCOMPLETE
        : category;
    const statuses =
      statusCategory === "all" && dealCategories
        ? flatten(Object.keys(dealCategories).map((c) => dealCategories[c]))
        : dealCategories?.[statusCategory] || [];

    if (statuses && statuses.length > 0) {
      Object.keys(statuses).forEach((key) => {
        statuses[key].label =
          DEAL_CATEGORY_FILTERS_MAPPING[statuses[key].value.toUpperCase()];
      });
    }
    return (statuses || [])
      .sort((a, b) => a.value.localeCompare(b.value))
      .map(({ id, value, label }) => ({ value, key: id, label }));
  }, [dealCategories, dealStatuses, category]);

  const propertySelection = useMemo(
    () => [
      ...(properties || [])?.map(({ id, name }) => ({
        label: name,
        key: `${id}`,
      })),
      {
        label: "Unassigned",
        key: "0",
      },
    ],
    [properties]
  );

  const lastLumenResultSelection = useMemo(() => {
    if (!distinctLumenResults) return [];

    // First, remove duplicates and null values
    const uniqueResults = Array.from(
      new Set(distinctLumenResults.filter(Boolean))
    );

    // Create the options array
    const options = uniqueResults
      .filter(
        (score) =>
          ![
            "SO_MUCH_EMPTY",
            "STILL_CALCULATING",
            "NOT_APPLICABLE",
            "N/A",
          ].includes(score)
      ) // Filter out special cases
      .map((score: string) => {
        // Try to use predefined mapping first
        const mappedLabel = LumenLabelByScore[score as LumenScore];
        if (mappedLabel) {
          return {
            key: score,
            label: mappedLabel.toUpperCase(),
          };
        }

        // Fallback: Clean up the raw score string
        return {
          key: score,
          label: score.replace(/_/g, " ").toUpperCase(),
        };
      });

    // Add "In Progress" option if either SO_MUCH_EMPTY or STILL_CALCULATING exists
    if (
      uniqueResults.some((score) =>
        ["SO_MUCH_EMPTY", "STILL_CALCULATING"].includes(score)
      )
    ) {
      options.push({
        key: "SO_MUCH_EMPTY,STILL_CALCULATING",
        label: "IN PROGRESS",
      });
    }

    // Always add "Not Applicable" option
    options.push({
      key: LumenScore.NOT_APPLICABLE,
      label: "NOT APPLICABLE",
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [distinctLumenResults]);

  const noFiltersApplied = !Object.keys(filters).some((key) => filters[key]);
  const loading =
    isDealCategoriesLoading ||
    isDealsLoading ||
    isDealsCountsLoading ||
    isDealsCountsFetching ||
    isDealsFetching ||
    isFiltersLoading;

  const changeCategory = (value: string) => {
    setCategory(value);
    updateFilters({
      dealStatus: [],
    });
  };

  const agentSelection = useMemo(
    () => [
      ...(agents || []).map((agent) => ({
        key: agent.id,
        label: agent.name + (agent.isCurrent ? " (me)" : ""),
      })),
      { key: "unassigned", label: "Unassigned" },
    ],
    [agents]
  );

  const guarantorStatusSelection = useMemo(
    () => activeCompany?.enableCosign ? [
      { key: "approved", label: "Cosign approved" },
      { key: "canceled", label: "Cosign cancelled" },
      { key: "denied", label: "Cosign denied" },
      { key: "expired", label: "Cosign expired" },
      // { key: "initiated", label: "Current application has cosign initiation" },
      { key: "pending", label: "Cosign pending" },
    ] : [],
    [activeCompany?.enableCosign]
  );

  const recommendationStatusSelection = useMemo(
    () =>
      buildRecommendationStatusOptions(
        vero1FiltersActive,
        lastLumenResultSelection
      ),
    [vero1FiltersActive, lastLumenResultSelection]
  );

  return (
    <Deals
      deals={deals?.results}
      dealsCount={deals?.count}
      refetchDeals={refetchDeals}
      updateFilters={updateFilters}
      updatedAllFilters={updatedAllFilters}
      filters={filters}
      pagination={pagination}
      hasInputValue={Boolean(filters.unitPropertyOrApplicant)}
      setPagination={setPagination}
      dealStatuses={dealStatuses}
      dealCategories={dealCategories}
      isFiltersLoading={isFiltersLoading}
      isDealsCountsLoading={isDealsCountsLoading}
      loading={loading}
      onTableChange={onTableChange}
      menuItems={menuItems}
      activeItem={category}
      onShowArchivedCheckboxChange={() => setShowArchived(!showArchived)}
      showArchived={showArchived}
      onShowOnlyPaidFeesCheckboxChange={() =>
        setShowOnlyPaidFees((prevShowOnlyPaidFees) => !prevShowOnlyPaidFees)
      }
      showOnlyPaidFees={showOnlyPaidFees}
      isIdFraudRiskEnabled={sentilinkFiltersActive}
      initialOrderingColumn={initialOrderingColumn}
      disabledSearch={deals?.length === 0 && noFiltersApplied}
      disabledFilters={(deals?.length === 0 && noFiltersApplied) || loading}
      propertySelection={propertySelection}
      dealStatusSelection={dealStatusSelection}
      lumenResultSelection={lastLumenResultSelection}
      guarantorStatusSelection={guarantorStatusSelection}
      recommendationStatusSelection={recommendationStatusSelection}
      changeCategory={changeCategory}
      experianPrompt={experianPrompt}
      enableSearchService={enableSearchService}
      showSearchServiceResults={showSearchServiceResults}
      setShowSearchServiceResults={setShowSearchServiceResults}
      agentSelection={agentSelection}
      blockedByIdFraudFilterActive={blockedOverlayActive}
    />
  );
};

export default DealsContainer;
